import { LicenceId } from '@ps/platform-message-framework/common/LicenceId';

import type { EffectParams } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { CasinoGame, CasinoInterruptedGame, CasinoTable } from '@swf/core/types/CasinoGame';
import { JoinReason, PlayType } from '@swf/core/types/CasinoGame';

import {
  TXTWCL_N0_is_available_in_Play_Money__ELL,
  TXTWCL_N0_is_available_in_Real_Money__ELL,
  TXTWCL_Races_are_Real_Money_Only,
  TXTWCL_Races_are_available_in_Real_Mo_ELL_1
} from './launch.messages.json';
import { checkAuth } from './utils';

const isDirectLaunchAllowed = (license: LicenceId) =>
  ![LicenceId.DEFAULT, LicenceId.EU, LicenceId.UK].includes(license);

// TODO: refactor this if else if else if else if...
const launch = async (
  payload: {
    game?: CasinoGame;
    raceId?: string;
    playType?: PlayType;
    joinReason?: JoinReason;
    table?: CasinoTable | CasinoInterruptedGame;
    windowRef?: Window | null;
    fsbLaunch?: boolean;
    externalLaunch?: boolean;
    widgetId?: string;
  },
  { actions, dispatch, rootState }: EffectParams
) => {
  const isPlayMoney = select.currency.isPlayMoney(rootState);

  const {
    game,
    fsbLaunch,
    joinReason = JoinReason.Play,
    table,
    raceId,
    windowRef,
    externalLaunch,
    widgetId
  } = payload;
  const authSubject = checkAuth({ dispatch, rootState, actions });
  const license = select.account.getLicense(rootState);

  let tables = game?.tables || [];
  let { playType } = payload;
  const fsbTable = table || game?.tables[0];

  if (tables.length && !playType) {
    const hasMultiTable = tables.some((table) => table.playType === PlayType.Multi);
    const hasSingleTable = tables.some((table) => table.playType === PlayType.Single);

    if (!(hasMultiTable && hasSingleTable)) {
      playType = hasSingleTable ? PlayType.Single : PlayType.Multi;
    }
  }

  if (tables.length && playType) {
    tables = tables.filter((table) => table.playType === playType);
  }

  try {
    await dispatch.casino.gameSessionCheck({ game, playType, fsbLaunch, widgetId });
  } catch {
    return;
  }

  if (!raceId && game?.fsb && fsbTable && !fsbTable.playMoney && isDirectLaunchAllowed(license)) {
    dispatch.casino.fetchFirstFsb({ table: fsbTable, fsbLaunch, game });
  } else if (authSubject) {
    authSubject.subscribe(() => {
      if (externalLaunch && game) {
        dispatch.modal.enqueue({
          path: '/casino/game-info',
          locationState: { game, raceId, fsbLaunch }
        });
      } else {
        dispatch.casino.launch(payload);
      }
    });
  } else if (
    select.session.isAuthenticated(rootState) &&
    !isPlayMoney &&
    !select.account.isEmailValidated(rootState)
  ) {
    dispatch.notification.setPendingNotifications();
  } else if (raceId && isPlayMoney) {
    dispatch.modal.enqueue({
      path: '/casino/error',
      locationState: {
        title: TXTWCL_Races_are_Real_Money_Only,
        message: TXTWCL_Races_are_available_in_Real_Mo_ELL_1
      }
    });
  } else if (
    (table && !table?.playMoney === isPlayMoney) ||
    tables.filter(({ playMoney }) => !playMoney === isPlayMoney).length
  ) {
    const translation = isPlayMoney
      ? TXTWCL_N0_is_available_in_Real_Money__ELL
      : TXTWCL_N0_is_available_in_Play_Money__ELL;
    dispatch.modal.enqueue({
      path: '/casino/error',
      locationState: { message: { ...translation, values: { 0: game?.name } } }
    });
  } else if (game && !table && tables.length > 1 && !playType) {
    dispatch.modal.enqueue({
      path: '/casino/player-selector',
      locationState: { game, raceId, widgetId }
    });
  } else if (game && !table && tables.length > 1) {
    dispatch.modal.enqueue({
      path: '/casino/stake-selector',
      locationState: { tables, game, raceId, widgetId }
    });
  } else if (table || tables.length) {
    if (externalLaunch && game) {
      dispatch.modal.enqueue({
        path: '/casino/game-info',
        locationState: { game, raceId, fsbLaunch }
      });
    } else {
      await dispatch.casino.play({
        game,
        table: table || tables[0],
        joinReason,
        playType,
        raceId,
        windowRef,
        widgetId
      });
    }
  }
};

export default launch;
